import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BRAND, IMAGES, SERVICES, ADDONS, QUOTE_URL } from "@/lib/constants";
import { Phone, Mail, ArrowUpRight, Sparkles, Clock, ShieldCheck, Music2, MessageCircleHeart } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FEATURES = [
  { icon: Clock, title: "Always Punctual", text: "Wheels-down arrivals, flight tracking, and a cabin chilled before you climb in." },
  { icon: ShieldCheck, title: "Discreet & Insured", text: "Background-checked chauffeurs. Privacy held to concierge standard." },
  { icon: Sparkles, title: "Crafted Experiences", text: "Champagne, flowers, red carpet — your night, set the way you imagined." },
  { icon: Music2, title: "Your Vibe, Your Way", text: "Tell us your mood. Quiet or chatty. Jazz or your own playlist — we adjust." },
];

const FAQS = [
  { q: "What areas do you serve?", a: "We are based in Pittsburgh, PA and serve the greater metro area, including PIT airport, surrounding suburbs, and special-event venues. Out-of-town transfers are available by request." },
  { q: "How far in advance should I book?", a: "We recommend booking at least 48 hours in advance for the best availability. Same-day bookings are accepted when our schedule permits — call us directly at " + BRAND.phoneVanity + "." },
  { q: "Can I add extras after I book?", a: "Yes. You can add champagne, flowers, chocolates, balloons, or a red-carpet rollout during the booking flow, or by replying to your confirmation email up to 24 hours before pickup." },
  { q: "Is my deposit refundable?", a: "Cancellations made more than 48 hours in advance are fully refundable. Within 48 hours, a 50% retainer applies. We will always work with you on rescheduling." },
  { q: "What does the 'mood selector' do?", a: "When you book, you tell us your conversation level, music preference, and any special notes (e.g., 'please be quiet, I am running late'). Your chauffeur reviews it before pickup so the ride feels exactly right." },
];

export default function Home() {
  return (
    <>
      <Header />

      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.fleet} alt="Cozy Keys black SUV" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-vignette" />
          <div className="absolute inset-0 grain-overlay" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-44 pb-32">
          <div className="overline fade-up" data-testid="hero-overline">{BRAND.city} · Private Chauffeur · Estd 2025</div>

          <h1 className="font-serif-display text-ivory text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mt-8 max-w-5xl fade-up fade-up-delay-1" data-testid="hero-title">
            The night is yours.
            <br />
            <span className="italic text-gold">We hold the keys.</span>
          </h1>

          <p className="text-ivory-muted text-lg lg:text-xl mt-10 max-w-xl leading-relaxed fade-up fade-up-delay-2" data-testid="hero-subtitle">
            A private chauffeur experience designed for the moments that matter — airport transfers, date nights, prom, concerts. Tell us your mood, choose your add-ons, arrive in quiet luxury.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-12 fade-up fade-up-delay-3">
            <Link to="/book" className="btn-gold px-9 py-4 inline-flex items-center gap-3" data-testid="hero-book-btn">
              Reserve Your Ride <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold px-9 py-4 inline-flex items-center gap-3" data-testid="hero-quote-btn">
              Get an Instant Quote <ArrowUpRight className="h-4 w-4" />
            </a>
            <a href="#services" className="text-ivory-muted hover:text-gold transition text-xs tracking-[0.25em] uppercase" data-testid="hero-services-btn">
              Explore Services
            </a>
          </div>

          <div className="mt-24 grid grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-8 max-w-4xl fade-up fade-up-delay-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="border-l border-gold-soft pl-5">
                <f.icon className="h-5 w-5 text-gold mb-3" />
                <div className="font-serif-display text-ivory text-xl mb-1">{f.title}</div>
                <div className="text-ivory-muted text-xs leading-relaxed">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-32 lg:py-40 max-w-[1400px] mx-auto px-6 lg:px-12" data-testid="services-section">
        <div className="grid lg:grid-cols-12 gap-16 mb-20">
          <div className="lg:col-span-5">
            <div className="overline mb-6">The Services</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight">
              Many ways to <em className="text-gold">arrive</em>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-ivory-muted text-lg leading-relaxed">
              Each ride is crafted with the same standard — black-on-black presentation, climate-controlled cabin, complimentary water, and a chauffeur whose first instinct is your comfort. <span className="text-gold/80">Pricing starts at the rates shown; final fare adjusts with mileage and time of day.</span>
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
          {SERVICES.map((s, i) => (
            <article key={s.id} className={`editorial-hover group ${i === 1 ? "lg:translate-y-12" : ""} ${i === 3 ? "lg:-translate-y-12" : ""}`} data-testid={`service-card-${s.id}`}>
              <div className="aspect-[4/5] overflow-hidden bg-emerald-deep">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
              </div>
              <div className="pt-6">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="font-serif-display text-2xl text-ivory">{s.title}</h3>
                  <span className="text-gold text-sm tracking-widest">STARTING AT ${s.price}</span>
                </div>
                <p className="text-ivory-muted text-sm leading-relaxed">{s.blurb}</p>
                <Link to={`/book?service=${s.id}`} className="inline-flex items-center gap-2 mt-4 text-gold text-xs tracking-[0.25em] uppercase hover:gap-3 transition-all" data-testid={`service-book-${s.id}`}>
                  Reserve <ArrowUpRight className="h-3 w-3" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* DATE NIGHT */}
      <section id="date-night" className="relative py-32 lg:py-40 bg-emerald-deep overflow-hidden" data-testid="date-night-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative editorial-hover">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={IMAGES.champagne} alt="Champagne for date night" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-void border border-gold-soft p-6 max-w-[260px]">
              <div className="overline mb-2">Pairs Beautifully</div>
              <div className="font-serif-display text-ivory text-2xl leading-snug">A bottle on ice, hand-tied roses, the city blurring by.</div>
            </div>
          </div>

          <div>
            <div className="overline mb-6">Date Night</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-8">
              Make the night feel <em className="text-gold">inevitable</em>.
            </h2>
            <p className="text-ivory-muted text-lg leading-relaxed mb-10">
              Pull up to her door in a black-on-black SUV. Step out, open the door, hand her a single rose. Inside, a chilled bottle of champagne and your favorite playlist already cued. We handle the choreography — you handle the moment.
            </p>

            <div className="space-y-4 mb-10">
              {ADDONS.slice(0, 4).map((a) => (
                <div key={a.id} className="flex items-center justify-between border-b border-gold-soft pb-3" data-testid={`date-addon-${a.id}`}>
                  <div>
                    <div className="text-ivory text-base">{a.label}</div>
                    <div className="text-ivory-muted text-xs">{a.hint}</div>
                  </div>
                  <div className="text-gold text-sm">+${a.price}</div>
                </div>
              ))}
            </div>

            <Link to="/book?service=date_night" className="btn-gold px-8 py-4 inline-flex items-center gap-3" data-testid="date-night-book-btn">
              Plan Your Date Night <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* MOOD / VIBE TEASE */}
      <section className="py-32 lg:py-40 max-w-[1400px] mx-auto px-6 lg:px-12" data-testid="mood-teaser-section">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6">Your Mood. Your Music. Your Ride.</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight">
              Tell us how you want to <em className="text-gold">feel</em>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-ivory-muted text-lg leading-relaxed">
              When you book, our concierge form asks a few quiet questions. Your answers reach the chauffeur before pickup. The ride begins in your key.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-16">
          <div className="border border-gold-soft bg-emerald-deep p-8" data-testid="mood-card-conversation">
            <MessageCircleHeart className="h-6 w-6 text-gold mb-4" />
            <div className="font-serif-display text-2xl text-ivory mb-2">Conversation Level</div>
            <p className="text-ivory-muted text-sm leading-relaxed">Quiet, minimal pleasantries, or a chatty companion — your call.</p>
          </div>
          <div className="border border-gold-soft bg-emerald-deep p-8" data-testid="mood-card-music">
            <Music2 className="h-6 w-6 text-gold mb-4" />
            <div className="font-serif-display text-2xl text-ivory mb-2">Music & Playlists</div>
            <p className="text-ivory-muted text-sm leading-relaxed">Pick a genre, share a Spotify or Apple Music link, or sit in silence.</p>
          </div>
          <div className="border border-gold-soft bg-emerald-deep p-8" data-testid="mood-card-comments">
            <Sparkles className="h-6 w-6 text-gold mb-4" />
            <div className="font-serif-display text-2xl text-ivory mb-2">Special Comments</div>
            <p className="text-ivory-muted text-sm leading-relaxed">"Running late, please drive faster." "Surprise her — keep quiet." We listen.</p>
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" className="relative py-32 lg:py-40 overflow-hidden" data-testid="fleet-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="overline mb-6">The Fleet</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-8">
              A blacked-out <em className="text-gold">Suburban</em>, kept hotel-clean.
            </h2>
            <p className="text-ivory-muted text-lg leading-relaxed mb-10">
              Tinted windows. Captain's chairs. A glove compartment of small comforts — bottled water, tissues, mints, phone chargers. The little things, executed every time.
            </p>
            <div className="grid grid-cols-3 gap-4 max-w-md">
              <div>
                <div className="font-serif-display text-3xl text-gold">7</div>
                <div className="text-ivory-muted text-xs uppercase tracking-widest">Passengers</div>
              </div>
              <div>
                <div className="font-serif-display text-3xl text-gold">24/7</div>
                <div className="text-ivory-muted text-xs uppercase tracking-widest">By request</div>
              </div>
              <div>
                <div className="font-serif-display text-3xl text-gold">100%</div>
                <div className="text-ivory-muted text-xs uppercase tracking-widest">Insured</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 editorial-hover">
            <div className="aspect-[5/4] overflow-hidden">
              <img src={IMAGES.interior} alt="Cozy Keys luxury cabin interior" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PROM */}
      <section id="prom" className="relative py-32 lg:py-40 bg-emerald-deep" data-testid="prom-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7 editorial-hover">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={IMAGES.prom} alt="Prom moment" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overline mb-6">Prom Set-Ups</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-8">
              An entrance worth <em className="text-gold">photographing</em>.
            </h2>
            <p className="text-ivory-muted text-lg leading-relaxed mb-10">
              Red carpet rolled out at the door. SUV detailed to a mirror finish. Their playlist already playing. Parents get a live arrival text. From doorstep to dance floor — your night becomes their memory.
            </p>
            <div className="border border-gold-soft p-6 mb-10">
              <div className="overline mb-3">Includes</div>
              <ul className="text-ivory-muted text-sm space-y-2">
                <li className="flex justify-between"><span>Red carpet rollout</span><span className="text-gold">Add-on</span></li>
                <li className="flex justify-between"><span>Custom playlist</span><span className="text-gold">Included</span></li>
                <li className="flex justify-between"><span>Bottled water + mints</span><span className="text-gold">Included</span></li>
                <li className="flex justify-between"><span>Parent SMS arrival update</span><span className="text-gold">Included</span></li>
              </ul>
            </div>
            <Link to="/book?service=prom" className="btn-gold px-8 py-4 inline-flex items-center gap-3" data-testid="prom-book-btn">
              Reserve Prom Night <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SPECIAL OCCASION */}
      <section id="special-occasion" className="relative py-32 lg:py-40 overflow-hidden" data-testid="special-occasion-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="overline mb-6">Special Occasion</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-8">
              Weddings, anniversaries, the <em className="text-gold">once-in-a-lifetime</em>.
            </h2>
            <p className="text-ivory-muted text-lg leading-relaxed mb-10">
              The day you've planned for years deserves a chauffeur who treats it like one. Multi-stop coordination — bridal suite to first look to ceremony to reception. Polished SUV, red carpet at the door, champagne chilled for the toast. We work with your planner so you don't have to think about logistics on the day that matters most.
            </p>

            <div className="border border-gold-soft p-6 mb-10 bg-emerald-deep">
              <div className="overline mb-4">Built for</div>
              <ul className="text-ivory-muted text-sm space-y-2.5">
                <li className="flex justify-between"><span>Weddings & receptions</span><span className="text-gold">Multi-stop</span></li>
                <li className="flex justify-between"><span>Anniversaries</span><span className="text-gold">Champagne included</span></li>
                <li className="flex justify-between"><span>Milestone birthdays</span><span className="text-gold">Curated playlist</span></li>
                <li className="flex justify-between"><span>Proposals & engagements</span><span className="text-gold">Discretion guaranteed</span></li>
                <li className="flex justify-between"><span>Bachelor / bachelorette</span><span className="text-gold">Group ride</span></li>
              </ul>
            </div>

            <Link to="/book?service=special_occasion" className="btn-gold px-8 py-4 inline-flex items-center gap-3" data-testid="special-occasion-book-btn">
              Reserve Your Moment <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2 editorial-hover">
            <div className="aspect-[5/4] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Special occasion" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block mt-6 text-right">
              <div className="overline opacity-70">Starting at</div>
              <div className="font-serif-display text-5xl text-gold">$350</div>
              <div className="text-[11px] text-ivory-muted tracking-widest uppercase">Final fare adjusts with mileage & duration</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-32 max-w-[1400px] mx-auto px-6 lg:px-12" data-testid="testimonials-section">
        <div className="overline mb-6 text-center">Quietly recommended</div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { q: "Showed up early, opened the door for my wife, had her favorite playlist on. Made me look like a hero.", n: "— Marcus T." },
            { q: "Booked them for our anniversary. The champagne and roses upgrade was worth every penny.", n: "— Alana R." },
            { q: "Drove our daughter to prom with her friends. Texted us at every stop. Worth more than we paid.", n: "— The Williams Family" },
          ].map((t, i) => (
            <blockquote key={i} className="border-t border-gold-soft pt-8" data-testid={`testimonial-${i}`}>
              <p className="font-serif-display text-2xl text-ivory leading-snug mb-6">"{t.q}"</p>
              <div className="overline">{t.n}</div>
            </blockquote>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-32 max-w-[900px] mx-auto px-6" data-testid="faq-section">
        <div className="text-center mb-16">
          <div className="overline mb-6">Frequently asked</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-ivory leading-tight">Answered, before you ask.</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-4">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border border-gold-soft px-6 bg-emerald-deep" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-ivory text-base font-serif-display hover:text-gold py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-ivory-muted text-sm leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA Strip */}
      <section className="py-24 border-t border-gold-soft bg-emerald-deep" data-testid="cta-strip">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-10 items-center">
          <h3 className="font-serif-display text-4xl md:text-5xl text-ivory leading-tight">
            Ready to <em className="text-gold">arrive</em> the way you imagined?
          </h3>
          <div className="flex flex-wrap items-center gap-6 md:justify-end">
            <a href={`tel:${BRAND.phone}`} className="flex items-center gap-3 text-ivory hover:text-gold" data-testid="cta-phone">
              <Phone className="h-4 w-4 text-gold" /><span className="tracking-widest text-sm">{BRAND.phoneVanity}</span>
            </a>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-ivory hover:text-gold" data-testid="cta-email">
              <Mail className="h-4 w-4 text-gold" /><span className="text-sm">{BRAND.email}</span>
            </a>
            <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold px-7 py-4" data-testid="cta-quote">Get a Quote</a>
            <Link to="/book" className="btn-gold px-8 py-4" data-testid="cta-book">Book Now</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
